"use client"

import { useEffect, useRef, useCallback, useState } from "react"
import { useSettingsStore } from "@/lib/settings-store"
import { useBlacklistStore } from "@/lib/blacklist-store"
import { useTagFiltersStore } from "@/lib/tag-filters-store"
import { useSavedTagsStore } from "@/lib/saved-tags-store"
import { useTagCustomizationsStore } from "@/lib/tag-customizations-store"
import { useTagAliasesStore } from "@/lib/tag-aliases-store"
import { useTagSetStore } from "@/lib/tag-set-store"
import { useUserRatingsStore } from "@/lib/user-ratings-store"
import { useFoldersStore } from "@/lib/folders-store"
import { useMediaPlayerSettingsStore } from "@/lib/media-player-settings-store"
import { isPremiumTier } from "@/lib/script-limits"
import { collectExtras, applyExtras } from "@/lib/backup-extras"
import { toast } from "sonner"

// ── Serialise ────────────────────────────────────────────────────────────────

function collectPayload() {
  const bl  = useBlacklistStore.getState()
  const tf  = useTagFiltersStore.getState()
  const st  = useSavedTagsStore.getState()
  const tc  = useTagCustomizationsStore.getState()
  const ta  = useTagAliasesStore.getState()
  const ts  = useTagSetStore.getState()
  const ur  = useUserRatingsStore.getState()
  const fd  = useFoldersStore.getState()
  const mp  = useMediaPlayerSettingsStore.getState()
  const set = useSettingsStore.getState()

  // Folders: persist structure only (no full post data — too large)
  const folders = Array.from(fd.folders.entries()).map(([id, folder]) => ({
    id,
    name: folder.name,
    description: folder.description,
    createdAt: folder.createdAt,
    updatedAt: folder.updatedAt,
    postIds: Array.from(folder.postIds),
  }))

  // Settings: exclude credentials — don't store passwords in our DB
  const settings = {
    loadingMode: set.loadingMode,
    postsPerPage: set.postsPerPage,
    imageQuality: set.imageQuality,
    gridThumbnailQuality: set.gridThumbnailQuality,
    autoplayVideos: set.autoplayVideos,
    handMode: set.handMode,
    imageNavDirection: set.imageNavDirection,
    infoSwipeDirection: set.infoSwipeDirection,
    gridLayout: set.gridLayout,
    gridFlowDirection: set.gridFlowDirection,
    gridGap: set.gridGap,
    gridColumns: set.gridColumns,
    borderRadius: set.borderRadius,
    showButtonLabels: set.showButtonLabels,
    hideScrollbar: set.hideScrollbar,
    shuffleMode: set.shuffleMode,
    smartShuffleMode: set.smartShuffleMode,
    stealthMode: set.stealthMode,
    saveSearchHistory: set.saveSearchHistory,
    saveViewHistory: set.saveViewHistory,
    saveDownloadHistory: set.saveDownloadHistory,
    cacheImages: set.cacheImages,
    blurThumbnails: set.blurThumbnails,
    theme: set.theme,
    mouseWheelBehavior: set.mouseWheelBehavior,
    enableMousePan: set.enableMousePan,
    panSensitivity: set.panSensitivity,
    showScrollButtons: set.showScrollButtons,
    searchBarPosition: set.searchBarPosition,
    performanceMode: set.performanceMode,
    selectedSites: set.selectedSites,
    // Newer settings, sources on/off, custom sources (no keys), script overlay look: kept inside this JSON column.
    extras: collectExtras(),
  }

  const { playbackSpeed, loop, muted, preservesPitch, audioScrubbing,
          isReversed, showScriptOverlay, overlayOpacity, overlayStyle, videoHeight,
          autoHideControls } = mp

  return {
    blacklist: {
      tags: bl.blacklistedTags,
      users: bl.blacklistedUsers,
      rules: bl.rules,
      enabled: bl.enableBlacklist,
    },
    tag_filters: {
      tagFilters: tf.tagFilters,
      enableFilters: tf.enableFilters,
      filterMode: tf.filterMode,
    },
    saved_tags: st.savedTags,
    tag_customizations: tc.customizations,
    tag_aliases: ta.aliases,
    tag_sets: {
      sets: ts.sets,
      tagPresets: ts.tagPresets,
      groups: ts.groups,
    },
    ratings: {
      ratings: ur.ratings,
      downloads: ur.downloads,
    },
    folders,
    settings,
    media_settings: {
      playbackSpeed, loop, muted, preservesPitch, audioScrubbing,
      isReversed, showScriptOverlay, overlayOpacity, overlayStyle,
      videoHeight, autoHideControls,
    },
  }
}

// ── Apply ────────────────────────────────────────────────────────────────────

function applyPayload(data: Record<string, unknown>) {
  const is = (v: unknown): v is Record<string, unknown> =>
    v !== null && typeof v === "object" && !Array.isArray(v)
  const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : [])

  if (is(data.blacklist)) {
    useBlacklistStore.setState({
      blacklistedTags:  arr<string>(data.blacklist.tags),
      blacklistedUsers: arr<string>(data.blacklist.users),
      rules:            arr(data.blacklist.rules),
      enableBlacklist:  typeof data.blacklist.enabled === "boolean" ? data.blacklist.enabled : true,
    })
  }

  if (is(data.tag_filters)) {
    useTagFiltersStore.setState({
      tagFilters:   arr(data.tag_filters.tagFilters),
      enableFilters: typeof data.tag_filters.enableFilters === "boolean" ? data.tag_filters.enableFilters : true,
      filterMode:   (data.tag_filters.filterMode as any) ?? "blacklist",
    })
  }

  if (Array.isArray(data.saved_tags)) {
    useSavedTagsStore.setState({ savedTags: data.saved_tags })
  }

  if (is(data.tag_customizations)) {
    useTagCustomizationsStore.setState({ customizations: data.tag_customizations as any })
  }

  if (Array.isArray(data.tag_aliases)) {
    useTagAliasesStore.setState({ aliases: data.tag_aliases as any })
  }

  if (is(data.tag_sets)) {
    const partial: Partial<ReturnType<typeof useTagSetStore.getState>> = {}
    if (Array.isArray(data.tag_sets.sets))       partial.sets       = data.tag_sets.sets as any
    if (Array.isArray(data.tag_sets.tagPresets))  partial.tagPresets = data.tag_sets.tagPresets as any
    if (Array.isArray(data.tag_sets.groups))      partial.groups     = data.tag_sets.groups as any
    useTagSetStore.setState(partial)
  }

  if (is(data.ratings)) {
    useUserRatingsStore.setState({
      ratings:   is(data.ratings.ratings)   ? (data.ratings.ratings as any)   : {},
      downloads: is(data.ratings.downloads) ? (data.ratings.downloads as any) : {},
    })
  }

  if (Array.isArray(data.folders)) {
    const { ImageFolder: _unused, ..._ } = {} as any
    const foldersMap = new Map<string, any>(
      (data.folders as any[]).map((f: any) => [
        f.id,
        {
          id:          f.id,
          name:        f.name ?? "Folder",
          description: f.description,
          createdAt:   f.createdAt ?? Date.now(),
          updatedAt:   f.updatedAt ?? Date.now(),
          postIds:     new Set<string>(Array.isArray(f.postIds) ? f.postIds : []),
          posts:       new Map(),
          croppedImages: new Map(),
        },
      ]),
    )
    useFoldersStore.setState({ folders: foldersMap })
  }

  if (is(data.settings)) {
    const s = data.settings as any
    const set = useSettingsStore.getState()
    if (s.loadingMode)          set.setLoadingMode?.(s.loadingMode)
    if (s.postsPerPage)         set.setPostsPerPage(s.postsPerPage)
    if (s.imageQuality)         set.setImageQuality(s.imageQuality)
    if (s.gridThumbnailQuality) (set as any).setGridThumbnailQuality?.(s.gridThumbnailQuality)
    if (s.autoplayVideos != null)  set.setAutoplayVideos(s.autoplayVideos)
    if (s.handMode)             set.setHandMode(s.handMode)
    if (s.imageNavDirection)    set.setImageNavDirection(s.imageNavDirection)
    if (s.infoSwipeDirection)   set.setInfoSwipeDirection(s.infoSwipeDirection)
    if (s.gridLayout)           set.setGridLayout(s.gridLayout)
    if (s.gridGap != null)      set.setGridGap(s.gridGap)
    if (s.gridColumns != null)  set.setGridColumns(s.gridColumns)
    if (s.borderRadius != null) set.setBorderRadius(s.borderRadius)
    if (s.showButtonLabels != null) set.setShowButtonLabels(s.showButtonLabels)
    if (s.hideScrollbar != null)    set.setHideScrollbar(s.hideScrollbar)
    if (s.shuffleMode != null)  set.setShuffleMode(s.shuffleMode)
    if (s.stealthMode != null)  set.setStealthMode(s.stealthMode)
    if (s.blurThumbnails != null)   set.setBlurThumbnails(s.blurThumbnails)
    if (s.theme)                set.setTheme(s.theme)
    if (s.selectedSites)        set.setSelectedSites(s.selectedSites)
    applyExtras(s.extras)
  }

  if (is(data.media_settings)) {
    const m = data.media_settings as any
    const mp = useMediaPlayerSettingsStore.getState()
    if (m.playbackSpeed != null)       mp.setPlaybackSpeed(m.playbackSpeed)
    if (m.loop        != null)         mp.setLoop(m.loop)
    if (m.muted       != null)         mp.setMuted(m.muted)
    if (m.showScriptOverlay != null)   mp.setShowScriptOverlay(m.showScriptOverlay)
    if (m.overlayOpacity != null)      mp.setOverlayOpacity(m.overlayOpacity)
    if (m.overlayStyle)                mp.setOverlayStyle(m.overlayStyle)
    if (m.videoHeight)                 mp.setVideoHeight(m.videoHeight)
    if (m.autoHideControls != null)    mp.setAutoHideControls(m.autoHideControls)
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

const SYNC_INTERVAL_MS = 2 * 60 * 1000 // 2 minutes

export function useUserDataSync(userId: string | null, subscriptionTier?: string | null) {
  const isPremium = isPremiumTier(subscriptionTier)
  const loadedRef   = useRef(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [lastSynced, setLastSynced] = useState<Date | null>(null)
  const [isSyncing, setIsSyncing]   = useState(false)
  // Guards the change-triggered push below against re-pushing data that was
  // just pulled from load() — set right before applyPayload() runs.
  const skipNextPushRef = useRef(false)
  // push() can be triggered from several places (the 2-minute interval, the
  // debounced change effect below, and manual syncNow()) with no guarantee
  // requests resolve in the order they were sent. Two overlapping POSTs
  // racing let the one that HAPPENS to respond last win, even if it was
  // sent first with older data — confirmed live: a push made right before
  // a POST from moments earlier finished, and the earlier/stale one landed
  // second and silently overwrote the newer one server-side. isPushingRef
  // serialises pushes so only one is ever in flight; pendingPushRef records
  // that another push was requested meanwhile and replays it (with
  // whatever is current by then) once the in-flight one finishes, instead
  // of running them concurrently.
  const isPushingRef = useRef(false)
  const pendingPushRef = useRef(false)

  const push = useCallback(async (silent = true) => {
    if (!userId || !isPremium) return
    if (isPushingRef.current) {
      pendingPushRef.current = true
      return
    }
    isPushingRef.current = true
    setIsSyncing(true)
    try {
      const res = await fetch("/api/user-data", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(collectPayload()),
      })
      if (!res.ok) throw new Error((await res.json()).error ?? "sync failed")
      setLastSynced(new Date())
      if (!silent) toast.success("Data synced to cloud")
    } catch (e: unknown) {
      if (!silent) toast.error("Cloud sync failed — your data is still saved locally")
      console.error("[user-data-sync] push error:", e)
    } finally {
      isPushingRef.current = false
      setIsSyncing(false)
      if (pendingPushRef.current) {
        pendingPushRef.current = false
        push(silent)
      }
    }
  }, [userId, isPremium])

  const load = useCallback(async () => {
    if (!userId || !isPremium) return
    try {
      const res = await fetch("/api/user-data")
      if (!res.ok) return
      const { data } = await res.json()
      if (data) {
        skipNextPushRef.current = true // applyPayload below is about to fire the
        // change-triggered push effect further down with data we JUST pulled —
        // pushing it straight back is redundant, not wrong, but worth skipping.
        applyPayload(data)
      }
    } catch (e) {
      console.error("[user-data-sync] load error:", e)
    }
  }, [userId, isPremium])

  // Load on login
  useEffect(() => {
    if (!userId || !isPremium) { loadedRef.current = false; return }
    if (loadedRef.current) return
    loadedRef.current = true
    load()
  }, [userId, isPremium, load])

  // Auto-push every 2 minutes while logged in + premium
  useEffect(() => {
    if (!userId || !isPremium) {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null }
      return
    }
    intervalRef.current = setInterval(() => push(true), SYNC_INTERVAL_MS)
    return () => {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null }
    }
  }, [userId, isPremium, push])

  // Push shortly after a synced setting actually changes, instead of only on
  // the 2-minute interval above. Without this, a real user hit this exactly:
  // change a setting (e.g. selectedSites), reload within that 2-minute
  // window, and load() on the new mount re-pulls the still-stale server
  // snapshot from before the change — silently discarding it. Debounced so a
  // burst of rapid changes (several checkbox toggles) doesn't fire a request
  // per click.
  useEffect(() => {
    if (!userId || !isPremium) return
    const debounceMs = 1500
    let timer: ReturnType<typeof setTimeout> | null = null
    const stores = [
      useSettingsStore,
      useBlacklistStore,
      useTagFiltersStore,
      useSavedTagsStore,
      useTagCustomizationsStore,
      useTagAliasesStore,
      useTagSetStore,
      useUserRatingsStore,
      useFoldersStore,
      useMediaPlayerSettingsStore,
    ] as const
    const schedulePush = () => {
      if (skipNextPushRef.current) { skipNextPushRef.current = false; return }
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => push(true), debounceMs)
    }
    const unsubs = stores.map((store) => store.subscribe(schedulePush))
    return () => {
      unsubs.forEach((unsub) => unsub())
      if (timer) clearTimeout(timer)
    }
  }, [userId, isPremium, push])

  const syncNow = useCallback(() => push(false), [push])

  return { syncNow, lastSynced, isSyncing, isPremium }
}
